import React, { useState } from 'react';
import { Clock, Package, Plus, Upload, X, Calendar, Phone, Mail, MapPin, Briefcase, Building, Euro, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';

const AddFormToggle = () => {
  const [activeForm, setActiveForm] = useState<'service' | 'product' | 'job'>('service');
  
  // Service form state
  const [serviceData, setServiceData] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    category: '',
    location: '',
    availability: {
      days: [] as string[],
      times: [] as string[]
    },
    contactPreference: '',
    tags: [] as string[],
    additionalInfo: ''
  });

  // Product form state
  const [productData, setProductData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    type: '',
    stock: '',
    tags: [] as string[]
  });

  // Job form state
  const [jobData, setJobData] = useState({
    title: '',
    company: '',
    description: '',
    salary: '',
    salaryType: 'per maand',
    workType: '',
    category: '',
    location: '',
    locationType: '',
    requirements: '',
    applicationDeadline: '',
    tags: [] as string[]
  });
  const [newTag, setNewTag] = useState('');

  const serviceCategories = [
    'Business & Consulting',
    'Creative Services',
    'Tech & Digital',
    'Professional Services',
    'Lifestyle & Wellness',
    'Education & Training'
  ];

  const productCategories = [
    'Digitale Producten',
    'Creative Producten', 
    'Business Tools',
    'Lifestyle Producten',
    'Beauty & Wellness',
    'Fashion & Accessoires'
  ];

  const jobCategories = [
    'Coaching & Wellness',
    'Marketing & Content',
    'Tech & Design',
    'Business & Strategy',
    'Fashion & Sustainability',
    'Data & Research',
    'Education & Training'
  ];

  const durationOptions = [
    { value: '30', label: '30 minuten' },
    { value: '45', label: '45 minuten' },
    { value: '60', label: '1 uur' },
    { value: '90', label: '1.5 uur' },
    { value: '120', label: '2 uur' },
    { value: '180', label: '3 uur' },
    { value: 'custom', label: 'Aangepast' }
  ];

  const weekDays = [
    'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'
  ];

  const timeSlots = [
    '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
  ];

  const workTypeOptions = [
    'Full-time',
    'Part-time',
    'Freelance',
    'Contract',
    'Stage/Internship'
  ];

  const locationTypeOptions = [
    'Remote',
    'In-person',
    'Hybrid'
  ];

  const salaryTypeOptions = [
    'per uur',
    'per maand',
    'per jaar',
    'per project'
  ];

  const handleServiceInputChange = (field: string, value: string) => {
    setServiceData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProductInputChange = (field: string, value: string) => {
    setProductData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleJobInputChange = (field: string, value: string) => {
    setJobData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAvailabilityChange = (type: 'days' | 'times', value: string, checked: boolean) => {
    setServiceData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [type]: checked 
          ? [...prev.availability[type], value]
          : prev.availability[type].filter(item => item !== value)
      }
    }));
  };

  const addTag = () => {
    if (newTag.trim()) {
      if (activeForm === 'service' && !serviceData.tags.includes(newTag.trim())) {
        setServiceData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag.trim()]
        }));
      } else if (activeForm === 'product' && !productData.tags.includes(newTag.trim())) {
        setProductData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag.trim()]
        }));
      } else if (activeForm === 'job' && !jobData.tags.includes(newTag.trim())) {
        setJobData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag.trim()]
        }));
      }
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    if (activeForm === 'service') {
      setServiceData(prev => ({
        ...prev,
        tags: prev.tags.filter(tag => tag !== tagToRemove)
      }));
    } else if (activeForm === 'product') {
      setProductData(prev => ({
        ...prev,
        tags: prev.tags.filter(tag => tag !== tagToRemove)
      }));
    } else {
      setJobData(prev => ({
        ...prev,
        tags: prev.tags.filter(tag => tag !== tagToRemove)
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (activeForm === 'service') {
      // Service validation
      if (!serviceData.name || !serviceData.description || !serviceData.price || !serviceData.category || !serviceData.duration) {
        toast.error('Vul alle verplichte velden in');
        return;
      }
      if (serviceData.availability.days.length === 0) {
        toast.error('Selecteer minimaal één dag beschikbaarheid');
        return;
      }
      console.log('Service form submitted:', serviceData);
      toast.success('Dienst succesvol toegevoegd!');
    } else if (activeForm === 'product') {
      // Product validation
      if (!productData.name || !productData.description || !productData.price || !productData.category) {
        toast.error('Vul alle verplichte velden in');
        return;
      }
      console.log('Product form submitted:', productData);
      toast.success('Product succesvol toegevoegd!');
    } else {
      // Job validation
      if (!jobData.title || !jobData.company || !jobData.description || !jobData.salary || !jobData.category || !jobData.workType) {
        toast.error('Vul alle verplichte velden in');
        return;
      }
      console.log('Job form submitted:', jobData);
      toast.success('Vacature succesvol toegevoegd!');
    }
  };

  const currentTags = activeForm === 'service' ? serviceData.tags : 
                     activeForm === 'product' ? productData.tags : jobData.tags;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6">
      {/* Form Toggle - Updated to include job option */}
      <div className="flex items-center gap-1 rounded-lg p-1 mb-8 w-full sm:w-fit mx-auto overflow-x-auto scrollbar-hide" style={{ backgroundColor: '#efe7dd' }}>
        <button
          onClick={() => setActiveForm('service')}
          className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-md transition-all flex-1 sm:flex-none whitespace-nowrap ${
            activeForm === 'service' 
              ? 'bg-white shadow-sm' 
              : 'hover:bg-white/50'
          }`}
          style={{ color: activeForm === 'service' ? '#837069' : '#b6a092' }}
        >
          <Clock className="h-4 w-4" />
          <span className="text-sm">Dienst</span>
        </button>
        <button
          onClick={() => setActiveForm('product')}
          className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-md transition-all flex-1 sm:flex-none whitespace-nowrap ${
            activeForm === 'product' 
              ? 'bg-white shadow-sm' 
              : 'hover:bg-white/50'
          }`}
          style={{ color: activeForm === 'product' ? '#837069' : '#b6a092' }}
        >
          <Package className="h-4 w-4" />
          <span className="text-sm">Product</span>
        </button>
        <button
          onClick={() => setActiveForm('job')}
          className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-md transition-all flex-1 sm:flex-none whitespace-nowrap ${
            activeForm === 'job' 
              ? 'bg-white shadow-sm' 
              : 'hover:bg-white/50'
          }`}
          style={{ color: activeForm === 'job' ? '#837069' : '#b6a092' }}
        >
          <Briefcase className="h-4 w-4" />
          <span className="text-sm">Vacature</span>
        </button>
      </div>

      {/* Form Content */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info Card */}
        <Card className="bg-white" style={{ border: '1px solid #efe7dd' }}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#837069' }}>
              <Plus className="h-5 w-5" style={{ color: '#a0897c' }} />
              Basis Informatie
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name" style={{ color: '#837069' }}>
                {activeForm === 'service' ? 'Dienst Naam' : 
                 activeForm === 'product' ? 'Product Naam' : 'Functietitel'} *
              </Label>
              <Input
                id="name"
                value={activeForm === 'service' ? serviceData.name : 
                       activeForm === 'product' ? productData.name : jobData.title}
                onChange={(e) => 
                  activeForm === 'service' ? handleServiceInputChange('name', e.target.value) :
                  activeForm === 'product' ? handleProductInputChange('name', e.target.value) :
                  handleJobInputChange('title', e.target.value)
                }
                placeholder={activeForm === 'service' ? "Bijv. Social Media Strategie Sessie" : 
                            activeForm === 'product' ? "Bijv. Social Media Templates Pack" :
                            "Bijv. UX Designer - Women's Health App"}
                className="mt-1"
                style={{ borderColor: '#efe7dd' }}
              />
            </div>

            {activeForm === 'job' && (
              <div>
                <Label htmlFor="company" style={{ color: '#837069' }}>Bedrijfsnaam *</Label>
                <Input
                  id="company"
                  value={jobData.company}
                  onChange={(e) => handleJobInputChange('company', e.target.value)}
                  placeholder="Bijv. Female Founders Network"
                  className="mt-1"
                  style={{ borderColor: '#efe7dd' }}
                />
              </div>
            )}
            <div>
              <Label htmlFor="description" style={{ color: '#837069' }}>Beschrijving *</Label>
              <Textarea
                id="description"
                value={activeForm === 'service' ? serviceData.description : 
                       activeForm === 'product' ? productData.description : jobData.description}
                onChange={(e) => 
                  activeForm === 'service' ? handleServiceInputChange('description', e.target.value) :
                  activeForm === 'product' ? handleProductInputChange('description', e.target.value) :
                  handleJobInputChange('description', e.target.value)
                }
                placeholder={activeForm === 'service' ? "Beschrijf je dienst, wat ga je doen voor je klant?" : 
                            activeForm === 'product' ? "Beschrijf je product, wat maakt het bijzonder?" :
                            "Beschrijf de functie, verantwoordelijkheden en wat jullie zoeken..."}
                className="mt-1 h-24"
                style={{ borderColor: '#efe7dd', color: '#837069' }}
              />
            </div>

            {/* Price/Salary and Duration/Type Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price" style={{ color: '#837069' }}>
                  {activeForm === 'job' ? 'Salaris (€)' : 'Prijs (€)'} *
                </Label>
                <Input
                  id="price"
                  type={activeForm === 'job' ? "text" : "number"}
                  step={activeForm === 'job' ? undefined : "0.01"}
                  value={activeForm === 'service' ? serviceData.price : 
                         activeForm === 'product' ? productData.price : jobData.salary}
                  onChange={(e) => 
                    activeForm === 'service' ? handleServiceInputChange('price', e.target.value) :
                    activeForm === 'product' ? handleProductInputChange('price', e.target.value) :
                    handleJobInputChange('salary', e.target.value)
                  }
                  placeholder={activeForm === 'service' ? "75.00" : 
                              activeForm === 'product' ? "25.00" : "3.500-4.200"}
                  className="mt-1"
                  style={{ borderColor: '#efe7dd' }}
                />
              </div>
              
              {activeForm === 'service' ? (
                <div>
                  <Label style={{ color: '#837069' }}>Duur</Label>
                  <Select value={formData.duration || ''} onValueChange={(value) => handleInputChange('duration', value)}>
                    <SelectTrigger className="mt-1" style={{ backgroundColor: '#efe7dd', borderColor: '#d9cbbf' }}>
                      <SelectValue placeholder="Selecteer duur" />
                    </SelectTrigger>
                    <SelectContent style={{ backgroundColor: '#efe7dd', border: '1px solid #d9cbbf' }} className="z-[100]">
                      <SelectItem value="30min" style={{ backgroundColor: '#efe7dd' }}>30 minuten</SelectItem>
                      <SelectItem value="1h" style={{ backgroundColor: '#efe7dd' }}>1 uur</SelectItem>
                      <SelectItem value="1.5h" style={{ backgroundColor: '#efe7dd' }}>1.5 uur</SelectItem>
                      <SelectItem value="2h" style={{ backgroundColor: '#efe7dd' }}>2 uur</SelectItem>
                      <SelectItem value="3h" style={{ backgroundColor: '#efe7dd' }}>3 uur</SelectItem>
                      <SelectItem value="halve-dag" style={{ backgroundColor: '#efe7dd' }}>Halve dag</SelectItem>
                      <SelectItem value="hele-dag" style={{ backgroundColor: '#efe7dd' }}>Hele dag</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : activeForm === 'product' ? (
                <div>
                  <Label htmlFor="stock" style={{ color: '#837069' }}>Voorraad</Label>
                  <Input
                    id="stock"
                    type="number"
                    value={productData.stock}
                    onChange={(e) => handleProductInputChange('stock', e.target.value)}
                    placeholder="Onbeperkt laat leeg"
                    className="mt-1"
                    style={{ borderColor: '#efe7dd' }}
                  />
                </div>
              ) : (
                <div>
                  <Label style={{ color: '#837069' }}>Salaris Type *</Label>
                  <Select value={formData.salaryType} onValueChange={(value) => handleInputChange('salaryType', value)}>
                    <SelectTrigger style={{ backgroundColor: '#efe7dd', borderColor: '#d9cbbf' }}>
                      <SelectValue placeholder="Selecteer type" />
                    </SelectTrigger>
                    <SelectContent style={{ backgroundColor: '#efe7dd', border: '1px solid #d9cbbf' }} className="z-[100]">
                      <SelectItem value="per-uur" style={{ backgroundColor: '#efe7dd' }}>Per uur</SelectItem>
                      <SelectItem value="per-maand" style={{ backgroundColor: '#efe7dd' }}>Per maand</SelectItem>
                      <SelectItem value="per-jaar" style={{ backgroundColor: '#efe7dd' }}>Per jaar</SelectItem>
                      <SelectItem value="per-project" style={{ backgroundColor: '#efe7dd' }}>Per project</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div>
              <Label style={{ color: '#837069' }}>Categorie *</Label>
              <Select 
                value={activeForm === 'service' ? serviceData.category : 
                       activeForm === 'product' ? productData.category : jobData.category} 
                onValueChange={(value) => 
                  activeForm === 'service' ? handleServiceInputChange('category', value) :
                  activeForm === 'product' ? handleProductInputChange('category', value) :
                  handleJobInputChange('category', value)
                }
              >
                <SelectTrigger className="mt-1" style={{ borderColor: '#efe7dd', backgroundColor: 'white' }}>
                  <SelectValue placeholder="Kies een categorie" />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: 'white', border: '1px solid #efe7dd' }} className="z-[100]">
                  {(activeForm === 'service' ? serviceCategories : 
                    activeForm === 'product' ? productCategories : jobCategories).map((category) => (
                    <SelectItem key={category} value={category} style={{ backgroundColor: 'white' }}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {activeForm === 'product' && (
              <div>
                <Label style={{ color: '#837069' }}>Product Type</Label>
                <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)}>
                  <SelectTrigger style={{ backgroundColor: '#efe7dd', borderColor: '#d9cbbf' }}>
                    <SelectValue placeholder="Selecteer type" />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: '#efe7dd', border: '1px solid #d9cbbf' }} className="z-[100]">
                    <SelectItem value="digital" style={{ backgroundColor: '#efe7dd' }}>Digitaal Product</SelectItem>
                    <SelectItem value="physical" style={{ backgroundColor: '#efe7dd' }}>Fysiek Product</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {activeForm === 'job' && (
              <div>
                <Label style={{ color: '#837069' }}>Werktype</Label>
                <Select value={formData.workType || ''} onValueChange={(value) => handleInputChange('workType', value)}>
                  <SelectTrigger className="mt-1" style={{ backgroundColor: '#efe7dd', borderColor: '#d9cbbf' }}>
                    <SelectValue placeholder="Selecteer werktype" />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: '#efe7dd', border: '1px solid #d9cbbf' }} className="z-[100]">
                    <SelectItem value="full-time" style={{ backgroundColor: '#efe7dd' }}>Full-time</SelectItem>
                    <SelectItem value="part-time" style={{ backgroundColor: '#efe7dd' }}>Part-time</SelectItem>
                    <SelectItem value="freelance" style={{ backgroundColor: '#efe7dd' }}>Freelance</SelectItem>
                    <SelectItem value="contract" style={{ backgroundColor: '#efe7dd' }}>Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Service-specific cards */}
        {activeForm === 'service' && (
          <>
            {/* Location Card */}
            <Card className="bg-white" style={{ border: '1px solid #efe7dd' }}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#837069' }}>
                  <MapPin className="h-5 w-5" style={{ color: '#a0897c' }} />
                  Locatie & Levering
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label style={{ color: '#837069' }}>Hoe lever je je dienst?</Label>
                  <RadioGroup 
                    value={serviceData.location} 
                    onValueChange={(value) => handleServiceInputChange('location', value)}
                    className="mt-2 space-y-3"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="online" id="online" />
                      <Label htmlFor="online" className="text-sm sm:text-base" style={{ color: '#837069' }}>Online (Zoom, Teams, etc.)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="in-person" id="in-person" />
                      <Label htmlFor="in-person" className="text-sm sm:text-base" style={{ color: '#837069' }}>Op locatie / In-persoon</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="both" id="both" />
                      <Label htmlFor="both" className="text-sm sm:text-base" style={{ color: '#837069' }}>Beide mogelijk</Label>
                    </div>
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>

            {/* Availability Card - iPad Optimized */}
            <Card className="bg-white" style={{ border: '1px solid #efe7dd' }}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#837069' }}>
                  <Calendar className="h-5 w-5" style={{ color: '#a0897c' }} />
                  Beschikbaarheid
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label style={{ color: '#837069' }}>Welke dagen ben je beschikbaar? *</Label>
                  {/* iPad Optimized Days Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                    {weekDays.map((day) => (
                      <div key={day} className="flex items-center space-x-2">
                        <Checkbox
                          id={day}
                          checked={serviceData.availability.days.includes(day)}
                          onCheckedChange={(checked) => 
                            handleAvailabilityChange('days', day, checked as boolean)
                          }
                        />
                        <Label htmlFor={day} className="text-sm cursor-pointer" style={{ color: '#837069' }}>{day}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label style={{ color: '#837069' }}>Voorkeur tijden</Label>
                  {/* iPad Optimized Times Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-2">
                    {timeSlots.map((time) => (
                      <div key={time} className="flex items-center space-x-2">
                        <Checkbox
                          id={time}
                          checked={serviceData.availability.times.includes(time)}
                          onCheckedChange={(checked) => 
                            handleAvailabilityChange('times', time, checked as boolean)
                          }
                        />
                        <Label htmlFor={time} className="text-sm cursor-pointer whitespace-nowrap" style={{ color: '#837069' }}>{time}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Preferences Card */}
            <Card className="bg-white" style={{ border: '1px solid #efe7dd' }}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#837069' }}>
                  <Phone className="h-5 w-5" style={{ color: '#a0897c' }} />
                  Contact Voorkeur
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label style={{ color: '#837069' }}>Hoe kunnen klanten je bereiken?</Label>
                  <RadioGroup 
                    value={serviceData.contactPreference} 
                    onValueChange={(value) => handleServiceInputChange('contactPreference', value)}
                    className="mt-2 space-y-3"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="email" id="email" />
                      <Label htmlFor="email" className="flex items-center gap-2 text-sm sm:text-base" style={{ color: '#837069' }}>
                        <Mail className="h-4 w-4" />
                        Email voorkeur
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="phone" id="phone" />
                      <Label htmlFor="phone" className="flex items-center gap-2 text-sm sm:text-base" style={{ color: '#837069' }}>
                        <Phone className="h-4 w-4" />
                        Telefonisch contact
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="both-contact" id="both-contact" />
                      <Label htmlFor="both-contact" className="text-sm sm:text-base" style={{ color: '#837069' }}>Beide mogelijk</Label>
                    </div>
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>

            {/* Additional Info Card */}
            <Card className="bg-white" style={{ border: '1px solid #efe7dd' }}>
              <CardHeader>
                <CardTitle className="text-lg" style={{ color: '#837069' }}>Extra Informatie</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="additionalInfo" style={{ color: '#837069' }}>Aanvullende informatie</Label>
                  <Textarea
                    id="additionalInfo"
                    value={serviceData.additionalInfo}
                    onChange={(e) => handleServiceInputChange('additionalInfo', e.target.value)}
                    placeholder="Wat moeten klanten nog meer weten? Voorbereidingen, materialen, etc."
                    className="mt-1 h-20"
                    style={{ borderColor: '#efe7dd', color: '#837069' }}
                  />
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Job-specific cards */}
        {activeForm === 'job' && (
          <>
            {/* Location & Work Setup Card */}
            <Card className="bg-white" style={{ border: '1px solid #efe7dd' }}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#837069' }}>
                  <MapPin className="h-5 w-5" style={{ color: '#a0897c' }} />
                  Locatie & Werkomgeving
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="jobLocation" style={{ color: '#837069' }}>Locatie *</Label>
                  <Input
                    id="jobLocation"
                    value={jobData.location}
                    onChange={(e) => handleJobInputChange('location', e.target.value)}
                    placeholder="Bijv. Brussels, Antwerpen, Nederland"
                    className="mt-1"
                    style={{ borderColor: '#efe7dd' }}
                  />
                </div>
                <div>
                  <Label style={{ color: '#837069' }}>Werkmodel</Label>
                  <RadioGroup 
                    value={jobData.locationType} 
                    onValueChange={(value) => handleJobInputChange('locationType', value)}
                    className="mt-2 space-y-3"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="remote" id="remote" />
                      <Label htmlFor="remote" className="text-sm sm:text-base" style={{ color: '#837069' }}>Remote (Volledig op afstand)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="in-person" id="in-person-job" />
                      <Label htmlFor="in-person-job" className="text-sm sm:text-base" style={{ color: '#837069' }}>Op locatie (Kantoor)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="hybrid" id="hybrid" />
                      <Label htmlFor="hybrid" className="text-sm sm:text-base" style={{ color: '#837069' }}>Hybride (Mix van thuis en kantoor)</Label>
                    </div>
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>

            {/* Requirements & Qualifications Card */}
            <Card className="bg-white" style={{ border: '1px solid #efe7dd' }}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#837069' }}>
                  <Users className="h-5 w-5" style={{ color: '#a0897c' }} />
                  Vereisten & Kwalificaties
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="requirements" style={{ color: '#837069' }}>Vereisten</Label>
                  <Textarea
                    id="requirements"
                    value={jobData.requirements}
                    onChange={(e) => handleJobInputChange('requirements', e.target.value)}
                    placeholder="Bijv. - 3+ jaar ervaring in UX design&#10;- Kennis van Figma en Adobe Creative Suite&#10;- Bachelor in Design of gerelateerd veld&#10;- Ervaring met user research"
                    className="mt-1 h-32"
                    style={{ borderColor: '#efe7dd', color: '#837069' }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Application Details Card */}
            <Card className="bg-white" style={{ border: '1px solid #efe7dd' }}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#837069' }}>
                  <Calendar className="h-5 w-5" style={{ color: '#a0897c' }} />
                  Sollicitatie Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="deadline" style={{ color: '#837069' }}>Sluitingsdatum</Label>
                  <Input
                    id="deadline"
                    type="date"
                    value={jobData.applicationDeadline}
                    onChange={(e) => handleJobInputChange('applicationDeadline', e.target.value)}
                    className="mt-1"
                    style={{ borderColor: '#efe7dd' }}
                  />
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Tags Card */}
        <Card className="bg-white" style={{ border: '1px solid #efe7dd' }}>
          <CardHeader>
            <CardTitle className="text-lg" style={{ color: '#837069' }}>
              {activeForm === 'service' ? 'Tags & Specialisaties' : 
               activeForm === 'product' ? 'Tags' : 'Skills & Tags'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* iPad Optimized Tag Input */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder={activeForm === 'service' ? "Bijv. marketing, strategie, SEO..." : 
                            activeForm === 'product' ? "Voeg een tag toe" :
                            "Bijv. UX Design, Figma, User Research..."}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1"
                style={{ borderColor: '#efe7dd' }}
              />
              <Button 
                type="button" 
                onClick={addTag} 
                size="sm" 
                className="w-full sm:w-auto hover:opacity-90"
                style={{ backgroundColor: '#a0897c', color: 'white' }}
              >
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Toevoegen</span>
              </Button>
            </div>
            {currentTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {currentTags.map((tag) => (
                  <Badge 
                    key={tag} 
                    variant="secondary" 
                    className="flex items-center gap-1 text-sm"
                    style={{ backgroundColor: '#efe7dd', color: '#837069' }}
                  >
                    {tag}
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={() => removeTag(tag)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Images Card */}
        <Card className="bg-white" style={{ border: '1px solid #efe7dd' }}>
          <CardHeader>
            <CardTitle className="text-lg" style={{ color: '#837069' }}>
              {activeForm === 'job' ? 'Bedrijf Logo & Media' : 'Foto\'s & Media'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed rounded-lg p-6 sm:p-8 text-center" style={{ borderColor: '#d9cbbf' }}>
              <Upload className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-4" style={{ color: '#a0897c' }} />
              <p className="mb-2 text-sm sm:text-base" style={{ color: '#837069' }}>
                Sleep je {activeForm === 'job' ? 'bedrijfslogo' : 'foto\'s'} hier of klik om te uploaden
              </p>
              <p className="text-xs sm:text-sm" style={{ color: '#b6a092' }}>
                PNG, JPG tot 5MB - {activeForm === 'service' ? 'Laat je werk zien!' : 
                                   activeForm === 'product' ? 'Laat je product zien!' : 
                                   'Voeg je bedrijfslogo toe!'}
              </p>
              <Button 
                type="button" 
                variant="outline" 
                className="mt-4 w-full sm:w-auto hover:bg-white/50"
                style={{ borderColor: '#efe7dd', color: '#837069' }}
              >
                Selecteer {activeForm === 'job' ? 'Logo' : 'Foto\'s'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button - iPad Optimized */}
        <div className="flex justify-center pt-6">
          <Button 
            type="submit" 
            className="w-full sm:w-auto px-8 sm:px-12 py-3 text-base sm:text-lg hover:opacity-90"
            style={{ backgroundColor: '#a0897c', color: 'white' }}
          >
            {activeForm === 'service' ? 'Dienst Publiceren' : 
             activeForm === 'product' ? 'Product Publiceren' : 
             'Vacature Publiceren'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddFormToggle;